import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import moment from 'moment';
import qs from 'qs';

import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import range from 'lodash/range';

import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';
import createFilters from '../../shared/filters';
import createTable from './components/table';

const calendar = createCalendar({
	namespace: 'payoutLogPayments',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(6, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'payoutLogPayments',
	stateNodeName: 'filters',
	listOfFilters: [{
		name: 'affiliate_id',
		title: 'Affiliates',
		placeholder: 'Add affiliates',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATES
		}
	}, {
		name: 'admin_id',
		title: 'Admins',
		placeholder: 'Add admins',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.ADMINS
		}
	},
	{
		name: 'payout_system_id',
		title: 'Payout System',
		placeholder: 'Add payout system',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.PAYOUT_SYSTEMS
		}
	}]
});

const table = createTable({
	namespace: 'payoutLogPayments',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'payout-log-payments/fetch-data-success';
const FETCH_DATA_ERROR = 'payout-log-payments/fetch-data-error';
const SHOW_PROGRESS_BAR = 'payout-log-payments/show-progress-bar';
const GOTO_PAGE_SUCCESS = 'payout-log-payments/goto-page-success';
const TOGGLE_PROGRESS_BAR = 'payout-log-payments/toggle-progress-bar';
const CELL_CONFIG_FILTER_TOGGLE = 'payout-log-payments/cell-config-filter-toggle';
const SELECT_CURRENCY = 'payout-log-payments/select-currency';
const APPLY = 'payout-log-payments/apply';
const CLEAR = 'payout-log-payments/clear';

// permissions
export const allowedPermissions = {
	'view': 'payout_log_view'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	error: false,
	calendar: calendar.initialState,
	filters: filters.initialState,
	query: {
		date_range: {
			start: null,
			end: null
		}
	},
	pagination: {
		currentPage: 1,
		perPage: 100,
		total: null
	},
	table: table.initialState,
	cellConfigFilter: [
		{ name: 'all', placeholder: 'All', active: true },
		{ name: 'pending', placeholder: 'Pending', active: false },
		{ name: 'approved', placeholder: 'Approved', active: false },
		{ name: 'confirmed', placeholder: 'Confirmed', active: false },
		{ name: 'paid', placeholder: 'Paid', active: false },
		{ name: 'rejected', placeholder: 'Rejected', active: false }
	]
};

// actions
export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'payoutLogPayments');

			if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
				return dispatch(replace(`payout-log/payments?${qs.stringify(savedFilters)}`));
			}

			dispatch(request(getState, externalQuery)).then(() => {
				if (externalQuery.entity) {
					const filtersList = externalQuery.entity.filters || [];

					dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
				}
			});
		},

		toggleCellFilter: (type) => (dispatch, getState) => {
			dispatch({
				type: CELL_CONFIG_FILTER_TOGGLE,
				payload: type
			});

			dispatch(request(getState));
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('payoutLogPaymentsSearchForm')(getState());
			const search = searchForm && searchForm.search;

			dispatch({
				type: APPLY,
				payload: search
			});

			dispatch(actions.toggleProgressBar());
			dispatch(request(getState));
		},

		clear: () => dispatch =>  {
			dispatch(actions.toggleProgressBar());
			dispatch(replace('payout-log/payments'));
			dispatch({ type: CLEAR });
		},

		showProgressBar: () => ({ type: SHOW_PROGRESS_BAR }),

		toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),

		selectCurrency: id => (dispatch, getState) => {
			dispatch({
				type: SELECT_CURRENCY,
				payload: id
			});

			dispatch(actions.toggleProgressBar());
			dispatch(request(getState));
		},

		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			dispatch(request(getState));
		}
	},
	filters.actions,
	calendar.actions,
	table.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => initialState,
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data.rows,
				pagination: {
					...state.pagination,
					total: action.payload.data.total
				},
				showProgressBar: false,
				error: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[CLEAR]: state => initialState,
		[APPLY]: (state, action) => ({
			...state,
			pagination: initialState.pagination,
			search: action.payload
		}),
		[SELECT_CURRENCY]: (state, action) => ({
			...state,
			query: {
				...state.query,
				currency: action.payload
			}
		}),
		[CELL_CONFIG_FILTER_TOGGLE]: (state, action) => {
			const filterName = action.payload;

			const activeCondition = (entity, filterName) => {
				return  (filterName === 'all')
					? entity.name === filterName
					: entity.name === 'all'
						? false
						: entity.name === filterName ? !entity.active : entity.active;
			};

			const cellConfigFilter = state.cellConfigFilter.map(
				entity => ({
					...entity,
					active: activeCondition(entity, filterName)
				})
			);

			const selectedFilter = cellConfigFilter.filter(entity => entity.active && entity.name !== 'all');

			return {
				...state,
				cellConfigFilter: (selectedFilter.length === 0 && filterName !== 'all')
					? state.cellConfigFilter : cellConfigFilter
			};

		}
	},
	filters.reducers,
	calendar.reducers,
	table.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutLogPayments.data,
	getIntl: intl,
	getProgressBar: state => state.payoutLogPayments.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.payoutLogPayments.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.payoutLogPayments.filters,
		state => state
	)(state),
	getCellConfig: state => state.payoutLogPayments.cellConfigFilter,
	getCurrentDateRange: state => state.payoutLogPayments.query.date_range,
	getTable: table.selectors.getTable,
	getPagination: state => {
		const { perPage, total, currentPage } = state.payoutLogPayments.pagination;
		return {
			pages: range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getListOfCurrencies: state => {
		const listOfCurrency = state.root.lists['available-currencies'];
		const all = { id: undefined, text: 'All' };
		return [all, ...listOfCurrency];
	},
	getCurrentCurrency: state => state.payoutLogPayments.query.currency,
	toggleProgressBar: () => ({ type: TOGGLE_PROGRESS_BAR }),
	getSearchData: state => ({ search: state.payoutLogPayments.search }),
	getError: state => state.payoutLogPayments.error
};

// functions
const getRequestQuery = (state, externalQuery = {}) => {
	const {
		query, calendar, filters: { selectedFilters },
		pagination: { perPage, currentPage },
		table: { sortByColumn },
		cellConfigFilter
	} = cloneDeep(state);

	const params = cloneDeep(query);
	const dateRange = getDateRangeFromCalendar(calendar);
	const limit = perPage;
	const offset = (currentPage - 1) * perPage;
	const statusList = cellConfigFilter
		.filter((cell) => (cell.active && cell.name !== 'all'))
		.map((cell) => cell.name);

	params['status'] = statusList;
	params['limit'] = limit;
	params['offset'] = offset;
	params['sort'] = [sortByColumn.index, sortByColumn.direction];

	if (dateRange) {
		params['date_range'] = dateRange;
	}

	if (selectedFilters) {
		const filters = {};
		const inverseFilters = {};

		selectedFilters.forEach(filter => {
			const values = filter.value.map(f => f.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});

		if (!isEmpty(filters)) {
			params['filters'] = filters;
		}
	}

	return qs.stringify({ ...params, ...externalQuery });
};

function request(getState, externalQuery) {
	const query = getRequestQuery(getState().payoutLogPayments, externalQuery && externalQuery.query);

	return http({
		url: `${endpoints.PAYOUTS.LOGS}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});
}



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payout-log/payments/index.js